import Market from '@core/models/offer/Market';
import Odd from '@core/models/offer/Odd';
import { OfferEventDto } from '@core/mappers/offer/dtos/OfferEventDto';
import { transformOfferEvent } from '@core/mappers/offer/transformOfferEvent';
import { StructManager } from '@core/services/offer/StructManager';
import OfferEvent from '@core/models/offer/OfferEvent';

export function isPriceBoostMarket(market: Market): boolean {
    return market.odds.every((odd) => {
        return isPriceBoostOdd(odd);
    });
}

export function hasEventPriceBoostOdds(event: OfferEvent): boolean {
    return event.getOdds().some((odd) => {
        return isPriceBoostOdd(odd);
    });
}

export function isPriceBoostOdd(odd?: Odd) {
    if (!odd) {
        return false;
    }

    return isLegacyBetBoost(odd) || isAutomatedPriceBoost(odd);
}

export function getOriginalPrice(odd: Odd) {
    if (isAutomatedPriceBoost(odd)) {
        return parseOriginalPrice(odd.extra?.originalPrice, odd.id);
    } else if (isLegacyBetBoost(odd)) {
        return Number(odd.specifiers?.was_price);
    } else {
        return undefined;
    }
}

export function isSuperBoosted(odd: Odd): boolean {
    return odd.extra?.boostLevel === 'super';
}

function isNormalBoosted(odd: Odd): boolean {
    return odd.extra?.boostLevel === 'normal';
}

function hasSuperbet(odd: Odd): boolean {
    return !!odd.extra?.superbets;
}

function isSuperBoostedNonSuperbet(odd: Odd): boolean {
    return isSuperBoosted(odd) && !hasSuperbet(odd);
}

function isSuperBoostedSuperbet(odd: Odd): boolean {
    return isSuperBoosted(odd) && hasSuperbet(odd);
}

function isNormalBoostedNonSuperbet(odd: Odd): boolean {
    return isNormalBoosted(odd) && !hasSuperbet(odd);
}

function isNormalBoostedSuperbet(odd: Odd): boolean {
    return isNormalBoosted(odd) && hasSuperbet(odd);
}

// https://www.notion.so/superbet/Price-Boost-2-0-Tech-Spec-103032f852c580de9deec0fca66afba7?pvs=4#1a1032f852c580ce9da6eea9da31bbe8
export function getSortedOdds(odds: Odd[]) {
    const priority = (odd: Odd) => [
        isSuperBoostedSuperbet(odd),
        isSuperBoostedNonSuperbet(odd),
        isNormalBoostedSuperbet(odd),
        isNormalBoostedNonSuperbet(odd),
    ].map((value) => (value ? 1 : 0));

    return odds.sort((a, b) => {
        const priorityA = priority(a);
        const priorityB = priority(b);

        for (let i = 0; i < priorityA.length; i++) {
            if (priorityB[i] !== priorityA[i]) {
                return priorityB[i] - priorityA[i];
            }
        }

        return 0;
    });
}

function parseOriginalPrice(originalPriceSet?: string, targetOutcomeId?: number): number | undefined {
    if (!originalPriceSet) {
        return undefined;
    }

    const priceEntries = originalPriceSet.split(',');
    for (const priceEntry of priceEntries) {
        const [outcomeId, price] = priceEntry.split(':');
        if (Number(outcomeId) === targetOutcomeId) {
            return Number(price);
        }
    }

    return undefined;
}

/**
 * Manual price boost with structure:
 *  - tags: "bet_boost"
 *  - specifiers.was_price: original price value
 */
function isLegacyBetBoost(odd: Odd) {
    const hasTag = !!odd.tags?.split(',').includes('bet_boost');
    const hasSpecifier = !!odd.specifiers?.was_price;

    return hasTag && hasSpecifier;
}

/**
 * Automated price boost with structure:
 *  - tags: "price_boost"
 *  - extras:
 *    - boostLevel: "normal" | "super"
 *    - originalPrice: A double representing the original price at the time the ticket was submitted.
 */
function isAutomatedPriceBoost(odd: Odd) {
    const hasTag = !!odd.tags?.split(',').includes('price_boost');
    const hasOriginalPrice = !!odd?.extra?.originalPrice;
    const hasBoostLevel = !!odd?.extra?.boostLevel;

    return hasTag && hasBoostLevel && hasOriginalPrice;
}

export async function transformAndDenormalizeTopPriceBoostOdds(eventsDto: OfferEventDto[]) {
    const events = await Promise.all(eventsDto
        .filter((event) => {
            return new Date(event.utcDate) > new Date();
        })
        .map(async (event) => {
            const transformedEvent = transformOfferEvent(event);
            await StructManager.getInstance().denormalizeEvent(transformedEvent);
            return transformedEvent;
        }));
    return events.flatMap((event) =>
        event.getOdds().map((odd) => ({
            ...odd,
            tournament: event.tournamentName,
            displayValue: odd.name,
            matchDate: event.utcDate || event.date.toDateString(),
            team1: event.team1Name,
            team2: event.team2Name,
            eventId: event.id,
        })),
    );
}
